<template>
  <div class="toast-container">
    <transition-group name="toast">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast-notification" 
        :class="getToastClass(toast.type)"
      >
        <div class="toast-header">
          <div class="toast-icon">
            <span class="material-icons">{{ getToastIcon(toast.type) }}</span>
          </div>
          <div class="toast-title">{{ toast.title }}</div>
          <button class="toast-close" @click="removeToast(toast.id)">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="toast-message">{{ toast.message }}</div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'

const { toasts, removeToast } = useToast()

// Obtenir la classe CSS en fonction du type de notification
const getToastClass = (type) => {
  switch (type) {
    case 'success': return 'toast-success'
    case 'error': return 'toast-error'
    case 'warning': return 'toast-warning'
    case 'info': return 'toast-info'
    default: return 'toast-info'
  }
}

// Obtenir l'icône en fonction du type de notification
const getToastIcon = (type) => {
  switch (type) {
    case 'success': return 'check_circle'
    case 'error': return 'error'
    case 'warning': return 'warning'
    case 'info': return 'info'
    default: return 'info'
  }
}
</script>

<style scoped>
/* Variables de couleurs OpSolver */
:root {
  --turquoise-primary: #30C7B5;
  --turquoise-deep: #155751;
  --turquoise-soft: #5DD6C7;
  --turquoise-pale: #E0F7F4;
  --coral-soft: #FF7A68;
}

.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999 !important;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 350px;
  width: 100%;
}

.toast-notification {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
  z-index: 99999 !important;
  position: relative;
}

.toast-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.toast-icon {
  margin-right: 10px;
  display: flex;
  align-items: center;
}

.toast-title {
  flex-grow: 1;
  font-weight: 600;
  font-size: 0.95rem;
}

.toast-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.2s;
}

.toast-close:hover {
  color: #333;
}

.toast-message {
  padding: 12px 16px;
  font-size: 0.9rem;
  color: #555;
}

/* Types de notifications */
.toast-success {
  border-left: 4px solid var(--turquoise-primary);
}

.toast-success .toast-icon {
  color: var(--turquoise-primary);
}

.toast-error {
  border-left: 4px solid var(--coral-soft);
}

.toast-error .toast-icon {
  color: var(--coral-soft);
}

.toast-warning {
  border-left: 4px solid #f39c12;
}

.toast-warning .toast-icon {
  color: #f39c12;
}

.toast-info {
  border-left: 4px solid var(--turquoise-deep);
}

.toast-info .toast-icon {
  color: var(--turquoise-deep);
}

/* Animation */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
